import { motion } from "framer-motion";
import {
  Folder, ChevronRight, ChevronDown, Trash2, MoreVertical, Image as ImageIcon,
  Send, BookMarked, Pencil,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, getToken, API } from "@/lib/api";
import { DEFAULT_COVER } from "@/lib/constants";

export function coverUrl(book) {
  return book.cover_path ? `${API}/books/${book.id}/cover-img?auth=${getToken()}` : DEFAULT_COVER;
}

export function descendantIds(folderId, folders) {
  const out = [folderId];
  const stack = [folderId];
  while (stack.length) {
    const cur = stack.pop();
    folders.filter((f) => f.parent_id === cur).forEach((c) => {
      out.push(c.id);
      stack.push(c.id);
    });
  }
  return out;
}

export function ProgressBar({ percent }) {
  if (!percent) return null;
  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
      <div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, percent)}%` }} />
    </div>
  );
}

export function FolderSelect({ folders, value, onChange }) {
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid="folder-select"><SelectValue placeholder="No folder" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No folder</SelectItem>
        {folders.map((f) => (
          <SelectItem key={f.id} value={f.id}>{f.parent_id ? "— " : ""}{f.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function buildVisibleFolders(folders, expanded) {
  const out = [];
  const walk = (list, depth) => {
    list.forEach((f) => {
      const kids = folders.filter((c) => c.parent_id === f.id);
      out.push({ folder: f, depth, hasChildren: kids.length > 0 });
      if (expanded[f.id]) walk(kids, depth + 1);
    });
  };
  walk(folders.filter((f) => !f.parent_id), 0);
  return out;
}

export function FolderRow({ folder, depth, hasChildren, isOpen, onToggleExpand, selected, onSelect, onDelete, onRename }) {
  const cls = selected === folder.id
    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
    : "hover:bg-blue-50 dark:hover:bg-blue-900/20";
  return (
    <div data-testid="folder-item" className={`group flex items-center gap-1 pr-1 rounded-md text-sm transition-colors ${cls}`}
      style={{ paddingLeft: `${depth * 12 + 4}px` }}>
      <button className="p-1 text-slate-400" onClick={onToggleExpand}>
        {hasChildren ? (isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />) : <span className="w-3 inline-block" />}
      </button>
      <button className="flex-1 flex items-center gap-2 py-1.5 text-left min-w-0" onClick={() => onSelect(folder.id)}>
        <Folder className="h-4 w-4 text-blue-500 fill-blue-200 dark:fill-blue-900 shrink-0" />
        <span className="truncate">{folder.name}</span>
      </button>
      <button data-testid="folder-rename" className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-indigo-600" onClick={() => onRename(folder)}>
        <Pencil className="h-3 w-3" />
      </button>
      <button data-testid="folder-delete" className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500" onClick={() => onDelete(folder.id)}>
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function BookMenu({ isAdmin, onEdit, onLend, onOpen, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="book-menu" className="p-1.5 text-slate-400 hover:text-slate-700"><MoreVertical className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isAdmin && <DropdownMenuItem onClick={onEdit}><ImageIcon className="h-4 w-4 mr-2" />Edit / Cover</DropdownMenuItem>}
        {isAdmin && <DropdownMenuItem onClick={onLend}><Send className="h-4 w-4 mr-2" />Lend</DropdownMenuItem>}
        <DropdownMenuItem onClick={onOpen}><BookMarked className="h-4 w-4 mr-2" />Open</DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem data-testid="book-delete" onClick={onDelete} className="text-red-600 focus:text-red-600">
            <Trash2 className="h-4 w-4 mr-2" />Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function BookCard({ book, isAdmin, selected, onToggle, onOpen, onEdit, onLend, onDelete }) {
  return (
    <motion.div whileHover={{ y: -4 }} className="group relative" data-testid="book-card">
      {isAdmin && (
        <div className="absolute top-2 left-2 z-10">
          <Checkbox data-testid="book-select" checked={selected} onCheckedChange={onToggle}
            className="bg-white border-slate-300 data-[state=checked]:bg-indigo-600" />
        </div>
      )}
      <div className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="rounded-md bg-white/90 dark:bg-slate-800/90">
          <BookMenu isAdmin={isAdmin} onEdit={onEdit} onLend={onLend} onOpen={onOpen} onDelete={onDelete} />
        </div>
      </div>
      <button onClick={onOpen} className="block w-full text-left">
        <div className="relative aspect-[2/3] rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm group-hover:shadow-md transition-shadow">
          <img src={coverUrl(book)} alt={book.title} className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = DEFAULT_COVER; }} />
          {book.borrowed_to && (
            <span className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-semibold">On loan</span>
          )}
          <ProgressBar percent={book.progress} />
        </div>
      </button>
      <div className="mt-2">
        <p className="text-sm font-semibold truncate text-slate-900 dark:text-slate-100">{book.title}</p>
        <p className="text-xs text-slate-500 truncate">{book.author || "Unknown author"}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          {book.material_type && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">{book.material_type}</span>
          )}
          {book.progress > 0 && <span className="text-[11px] text-indigo-600">{Math.round(book.progress)}% read</span>}
        </div>
      </div>
    </motion.div>
  );
}

export function BookRow({ book, folders, isAdmin, selected, onToggle, onOpen, onEdit, onLend, onDelete }) {
  const folderName = folders.find((f) => f.id === book.folder_id)?.name;
  return (
    <div data-testid="book-row" className="flex items-center gap-3 p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-indigo-300 transition-colors">
      {isAdmin && <Checkbox data-testid="book-select" checked={selected} onCheckedChange={onToggle} />}
      <img src={coverUrl(book)} alt="" className="h-12 w-9 object-cover rounded" onError={(e) => { e.currentTarget.src = DEFAULT_COVER; }} />
      <button onClick={onOpen} className="flex-1 min-w-0 text-left">
        <p className="text-sm font-semibold truncate text-slate-900 dark:text-slate-100">{book.title}</p>
        <p className="text-xs text-slate-500 truncate">{book.author || "Unknown"} {book.material_type && `· ${book.material_type}`} {folderName && `· ${folderName}`}</p>
      </button>
      <span className="text-xs text-indigo-600 w-14 text-right">{book.progress ? `${Math.round(book.progress)}%` : ""}</span>
      <BookMenu isAdmin={isAdmin} onEdit={onEdit} onLend={onLend} onOpen={onOpen} onDelete={onDelete} />
    </div>
  );
}
